import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getIcdDescription } from "../../../../api/icdDescription/getIcdDescription";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { EventsName } from "../../../../enums/eventsName";
import { formatJcodeIcdFromAuthOnFile } from "../../../../pages/singleOrderLayout/utils/getJCode";
import { RootState } from "../../../../redux/store/store";
import { usePrimaryIcdCodes } from "../../../../utils/getPrimaryDiagnosisCodes";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import {
  DX_CODE_CHANGE_OTHER_OPTION,
  ICD_DESCRIPTION_DEBOUNCE_MS,
  ICD_DESCRIPTION_LOADING_PLACEHOLDER,
} from "../constants";
import { ClinicalDataTableProps, TableData } from "../types";
import {
  buildDxCodeDropdownSelectedAdditionalData,
  ClinicalDataAnalyticsContext,
} from "../utils/buildClinicalDataAnalytics";
import { generateTableData } from "../utils/generateTableData";
import { resolveInitialPrimaryDxSelection } from "../utils/resolveInitialPrimaryDxSelection";
import {
  dropdownOptions,
  ENTER_MANUALLY_OPTION,
  NONE_OF_THE_ABOVE_OPTION,
} from "../utils/saveNewPrimaryDiagnosisCode";

/**
 * Owns clinical data table UI state, initialization, derived values, and
 * primary Dx selection handlers.
 *
 * @param props - Clinical data table modal props.
 * @return State, derived data, and handlers for the modal UI.
 */
export const useClinicalDataTable = (props: ClinicalDataTableProps) => {
  const {
    submissionData,
    fetchAll,
    clinicalConditions,
    generalOrderDetails,
    authOnFileDetails,
    coverageDetails,
  } = useDataAccumulatorForSingleOrder();

  const primaryIcdCodes = usePrimaryIcdCodes(
    submissionData?.submission ?? undefined,
  );

  const [tableData, setTableData] = useState<TableData[]>([]);
  const [selectedPrimaryCode, setSelectedPrimaryCode] = useState<string>("");
  const [defaultPrimaryCode, setDefaultPrimaryCode] = useState("");
  const icdSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // Monotonic id of the latest ICD description lookup; used to discard
  // out-of-order/stale responses so only the most recent request writes state.
  const icdRequestIdRef = useRef(0);
  const [selectedPrimaryCodeDescription, setSelectedPrimaryCodeDescription] =
    useState<string>("");
  const [manualPrimaryCode, setManualPrimaryCode] = useState<string>("");
  const [userReason, setUserReason] = useState<string>("");
  const [userSource, setUserSource] = useState<string>("");
  const [otherReason, setOtherReason] = useState<string>("");
  const [otherSource, setOtherSource] = useState<string>("");
  const hasInitializedSelectionRef = useRef(false);

  const isManualEntry = selectedPrimaryCode === ENTER_MANUALLY_OPTION;
  const isManualEntryMissing = isManualEntry && !manualPrimaryCode.trim();
  // In manual entry the effective code is the typed value, not the sentinel;
  // otherwise it is the selected dropdown value. Comparing the sentinel against
  // the default would spuriously flag a pre-existing custom code as "changed".
  const effectivePrimaryCode = isManualEntry
    ? manualPrimaryCode.trim()
    : selectedPrimaryCode;
  // The effective selected code differs from the originally assigned default,
  // meaning the user actually changed the primary Dx.
  const hasPrimaryDxChanged =
    !!effectivePrimaryCode && effectivePrimaryCode !== defaultPrimaryCode;
  // When "Other" is selected, the required value lives in the companion
  // textarea state while the dropdown state remains the persisted sentinel.
  const effectiveUserReason =
    userReason === DX_CODE_CHANGE_OTHER_OPTION ? otherReason : userReason;
  const effectiveUserSource =
    userSource === DX_CODE_CHANGE_OTHER_OPTION ? otherSource : userSource;
  const isReasonMissing = hasPrimaryDxChanged && !effectiveUserReason.trim();
  const isSourceMissing = hasPrimaryDxChanged && !effectiveUserSource.trim();

  const jcodeIcd = useMemo(
    () => formatJcodeIcdFromAuthOnFile(authOnFileDetails?.auth_on_file),
    [authOnFileDetails],
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const orgId = getOrgIdForFetchExternalWorklist() ?? "";

  const analyticsContext: ClinicalDataAnalyticsContext = useMemo(
    () => ({
      screenName: props.screenName,
      orgId,
      patientId:
        generalOrderDetails?.order?.demographics?.patient_id ??
        generalOrderDetails?.order?.demographics?.patient_mrn ??
        "",
      orderId: generalOrderDetails?.order?.order_id ?? "",
      userId: user?.id ?? "",
      userEmail: user?.email ?? "",
      payerName:
        coverageDetails?.coverages?.find(
          (coverage) => coverage.coverage_type === "primary",
        )?.payer_name ?? "",
      jcodeIcd,
      authOnFile: authOnFileDetails?.auth_on_file,
    }),
    [
      props.screenName,
      orgId,
      generalOrderDetails,
      user,
      coverageDetails,
      jcodeIcd,
      authOnFileDetails,
    ],
  );

  /**
   * Syncs patient Dx table rows and applies the initial primary Dx selection.
   * Falls back to manual entry when the saved code is absent from dropdown options.
   * Leaves the selector blank when the saved code is empty.
   */
  useEffect(() => {
    if (!clinicalConditions) {
      return;
    }

    const nextTableData = generateTableData(clinicalConditions) || [];
    setTableData(nextTableData);

    if (primaryIcdCodes.length === 0 || hasInitializedSelectionRef.current) {
      return;
    }

    const defaultCode = primaryIcdCodes[0];
    setDefaultPrimaryCode(defaultCode);

    const initialSelection = resolveInitialPrimaryDxSelection(
      defaultCode,
      nextTableData,
      submissionData?.submission?.primary_diagnosis_details,
    );

    if (initialSelection) {
      setSelectedPrimaryCode(initialSelection.selectedPrimaryCode);
      setManualPrimaryCode(initialSelection.manualPrimaryCode);
      setSelectedPrimaryCodeDescription(
        initialSelection.selectedPrimaryCodeDescription,
      );
    }

    hasInitializedSelectionRef.current = true;
  }, [
    clinicalConditions,
    primaryIcdCodes,
    submissionData?.submission?.primary_diagnosis_details,
  ]);

  useEffect(() => {
    return () => {
      if (icdSearchTimeoutRef.current) {
        clearTimeout(icdSearchTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Debounced ICD description lookup for the manual Dx code input.
   *
   * Each lookup is tagged with an incrementing request id so that only the most
   * recent request can write the description state ("latest request wins"),
   * preventing a slow earlier request from overwriting a newer code. On failure
   * the description is cleared so the input never stays stuck on the loading
   * placeholder.
   *
   * @param icdCode - The raw ICD code value typed by the user.
   */
  const debouncedFetchIcdDescription = useCallback((icdCode: string) => {
    if (icdSearchTimeoutRef.current) {
      clearTimeout(icdSearchTimeoutRef.current);
    }

    const trimmedCode = icdCode.trim();
    if (!trimmedCode) {
      icdRequestIdRef.current += 1;
      setSelectedPrimaryCodeDescription("");
      return;
    }

    setSelectedPrimaryCodeDescription(ICD_DESCRIPTION_LOADING_PLACEHOLDER);
    const requestId = (icdRequestIdRef.current += 1);
    icdSearchTimeoutRef.current = setTimeout(() => {
      getIcdDescription(trimmedCode)
        .then((description) => {
          if (requestId === icdRequestIdRef.current) {
            setSelectedPrimaryCodeDescription(description);
          }
        })
        .catch(() => {
          if (requestId === icdRequestIdRef.current) {
            setSelectedPrimaryCodeDescription("");
          }
        });
    }, ICD_DESCRIPTION_DEBOUNCE_MS);
  }, []);

  const dropdownOpts = useMemo(
    () => [
      ...dropdownOptions(tableData),
      { value: NONE_OF_THE_ABOVE_OPTION, label: "None of the above" },
      { value: ENTER_MANUALLY_OPTION, label: "Enter Manually" },
    ],
    [tableData],
  );

  const getRowClassName = useCallback(
    (row: TableData) => {
      return row.icd10_code && primaryIcdCodes.includes(row.icd10_code)
        ? "bg-tertiaryGreen-50"
        : "";
    },
    [primaryIcdCodes],
  );

  /**
   * Handles primary Dx dropdown selection and logs the analytics event.
   *
   * @param option - Selected dropdown option.
   */
  const handlePrimaryCodeChange = useCallback(
    (option: { name: string; value: string }) => {
      apiWorkerManager.enqueueAndProcess({
        patient_id: analyticsContext.patientId,
        event_name: EventsName.DX_CODE_DROPDOWN_SELECTED,
        org_id: analyticsContext.orgId,
        order_id: analyticsContext.orderId,
        user_id: analyticsContext.userId,
        user_email: analyticsContext.userEmail,
        additional_data: buildDxCodeDropdownSelectedAdditionalData(
          analyticsContext,
          {
            selectedDxCode: option.value,
            assignedPrimaryIcdCodes: primaryIcdCodes,
          },
        ),
      });
      icdRequestIdRef.current += 1;
      setSelectedPrimaryCode(option.value);
      setSelectedPrimaryCodeDescription("");
      setManualPrimaryCode("");
    },
    [analyticsContext, primaryIcdCodes],
  );

  const handleManualCodeChange = useCallback(
    (value: string) => {
      setManualPrimaryCode(value);
      debouncedFetchIcdDescription(value);
    },
    [debouncedFetchIcdDescription],
  );

  return {
    tableData,
    selectedPrimaryCode,
    defaultPrimaryCode,
    manualPrimaryCode,
    selectedPrimaryCodeDescription,
    primaryIcdCodes,
    isManualEntry,
    isManualEntryMissing,
    hasPrimaryDxChanged,
    userReason,
    userSource,
    otherReason,
    otherSource,
    isReasonMissing,
    isSourceMissing,
    setUserReason,
    setUserSource,
    setOtherReason,
    setOtherSource,
    dropdownOpts,
    getRowClassName,
    orgId,
    analyticsContext,
    handlePrimaryCodeChange,
    handleManualCodeChange,
    submissionData,
    fetchAll,
    generalOrderDetails,
    user,
  };
};
